import { ApiError, type Point, type Role } from '@postifoto/api-client';
import { type Permission, type PermissionGroup, PROBLEM_TYPES } from '@postifoto/contracts/client';
import { useMemo } from 'react';
import { useFormatter, useLocale, useTranslations } from 'use-intl';
import { isProblem, retryAfter } from '../lib/api-errors';

/** Понятный текст ошибки API по её типу (ADR-032), на языке сотрудника. */
export function useErrorMessage() {
  const t = useTranslations('errors');
  return (error: unknown): string => {
    if (isProblem(error, PROBLEM_TYPES.invalidCredentials)) return t('invalidCredentials');
    if (isProblem(error, PROBLEM_TYPES.invalidCode)) return t('invalidCode');
    if (isProblem(error, PROBLEM_TYPES.invalidPassword)) return t('invalidPassword');
    if (isProblem(error, PROBLEM_TYPES.accountLocked)) {
      return t('accountLocked', {
        minutes: Math.max(1, Math.ceil((retryAfter(error) ?? 60) / 60)),
      });
    }
    if (isProblem(error, PROBLEM_TYPES.rateLimited)) return t('rateLimited');
    if (isProblem(error, PROBLEM_TYPES.serviceBusy)) return t('serviceBusy');
    if (isProblem(error, PROBLEM_TYPES.forbidden)) return t('forbidden');
    if (isProblem(error, PROBLEM_TYPES.mfaUnavailable)) return t('mfaUnavailable');
    if (error instanceof ApiError && error.problem.type === 'about:blank' && error.problem.detail) {
      return error.problem.detail;
    }
    return t('generic');
  };
}

/** Текст нарушения политики пароля по коду из API. */
export function usePasswordPolicyMessage() {
  const t = useTranslations('errors.password');
  return (code: string | undefined): string | undefined => {
    if (!code) return undefined;
    return t.has(code as 'common') ? t(code as 'common') : t('common');
  };
}

/** Дата и время в часовом поясе инстанса; null — «никогда». */
export function useFormatDateTime() {
  const format = useFormatter();
  const t = useTranslations('common');
  return (value: string | null): string =>
    value
      ? format.dateTime(new Date(value), { dateStyle: 'short', timeStyle: 'short' })
      : t('never');
}

/** Название роли: у пресета — перевод по коду, у своей роли — её имя. */
export function useRoleName() {
  const t = useTranslations('roles');
  return (role: Role | undefined): string => {
    if (!role) return '?';
    if (role.presetCode) {
      return t.has(role.presetCode as 'admin') ? t(role.presetCode as 'admin') : role.presetCode;
    }
    return role.name ?? '?';
  };
}

/** Название точки; null — «Все точки». */
export function usePointName() {
  const t = useTranslations('common');
  return (points: Point[], pointId: string | null): string =>
    pointId === null ? t('allPoints') : (points.find((point) => point.id === pointId)?.name ?? '?');
}

/** Названия дней недели ISO (1 — понедельник) на языке сотрудника — из Intl, без каталога. */
export function useWeekdayNames() {
  const locale = useLocale();
  return useMemo(() => {
    // 2024-01-01 — понедельник; полдень UTC, чтобы день не сдвинулся ни в одном поясе.
    const days = [1, 2, 3, 4, 5, 6, 7].map((day) => new Date(Date.UTC(2024, 0, day, 12)));
    const long = new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' });
    const short = new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' });
    const capitalize = (value: string) =>
      value.charAt(0).toLocaleUpperCase(locale) + value.slice(1);
    return {
      long: days.map((day) => capitalize(long.format(day))),
      short: days.map((day) => capitalize(short.format(day))),
    };
  }, [locale]);
}

/** Название языка на нём самом: «русский», «eesti», «English». */
export function useLanguageName() {
  return (code: string): string => {
    const name = new Intl.DisplayNames([code], { type: 'language' }).of(code) ?? code;
    return name.charAt(0).toLocaleUpperCase(code) + name.slice(1);
  };
}

/** Местная дата YYYY-MM-DD как «ср, 14.10» на языке сотрудника. Полдень UTC — дата не сдвигается. */
export function useFormatLocalDay() {
  const format = useFormatter();
  return (date: string): string =>
    format.dateTime(new Date(`${date}T12:00:00Z`), {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
      timeZone: 'UTC',
    });
}

const BYTE_UNITS = [
  ['gigabyte', 1024 ** 3],
  ['megabyte', 1024 ** 2],
  ['kilobyte', 1024],
] as const;

/** Размер файла в единицах языка сотрудника: «5 МБ», «1,2 ГБ». */
export function useFormatBytes() {
  const format = useFormatter();
  return (bytes: number): string => {
    for (const [unit, size] of BYTE_UNITS) {
      if (bytes >= size) {
        const value = bytes / size;
        return format.number(value, {
          style: 'unit',
          unit,
          maximumFractionDigits: value < 10 ? 1 : 0,
        });
      }
    }
    return format.number(bytes, { style: 'unit', unit: 'byte' });
  };
}

/** Название права из каталога: ключ — код с `_` вместо точек (точка в use-intl — вложенность). */
export function usePermissionLabel() {
  const t = useTranslations('permissions');
  return (code: Permission): string => {
    const key = code.replaceAll('.', '_') as 'order_view';
    return t.has(key) ? t(key) : code;
  };
}

export function usePermissionGroupLabel() {
  const t = useTranslations('permissionGroups');
  return (group: PermissionGroup): string => t(group);
}

/** Сумма в минорных единицах → строка с валютой инстанса на языке сотрудника. */
export function useFormatMoney() {
  const format = useFormatter();
  return (minor: number, currency: string): string => {
    const digits =
      new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions()
        .maximumFractionDigits ?? 2;
    return format.number(minor / 10 ** digits, { style: 'currency', currency });
  };
}
